import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import _get from 'lodash/get';
import {
  WidgetTemplatesKindEnum,
  WidgetTemplatesVisibilityEnum,
  WidgetTemplateCreateInputObject,
  useWidgetTemplateCreateMutation,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import { GetUrlParams } from '@lib/enums/urls';
import { AttachmentIntendedUse } from '@lib/enums/attachments';
import toast from '@lib/components/Toast/Toast';
import prepareAttachmentInputs, {
  AttachmentInput,
} from 'routes/Home/utils/prepareAttachmentInputs';
import { APP_URLS } from 'constants/urls';

/**
 * Return type for useWidgetTemplateCreate hook
 */
interface UseWidgetTemplateCreateResult {
  /** Loading state of the create mutation */
  loading: boolean;
  /** Handler to cancel creation and navigate back */
  handleCancel: () => void;
  /** Handler to submit the form and create a widget template */
  handleSubmit: (values: Values) => Promise<void>;
}

/**
 * Prepares widget template attributes from form values
 * Handles different field structures based on widget kind
 */
function prepareWidgetTemplateAttributes(
  values: Values,
): WidgetTemplateCreateInputObject | null {
  const kindOption = _get(values, 'kind') as { value: string } | undefined;
  const kind = kindOption?.value as WidgetTemplatesKindEnum;

  if (!kind) return null;

  const attributes: WidgetTemplateCreateInputObject = {
    kind,
    title: _get(values, 'title') as string,
  };
  const attachmentsToSend: AttachmentInput[] = [];

  // Set visibility based on isPrivate toggle
  const isPrivate = _get(values, 'isPrivate') as boolean | undefined;
  attributes.visibility = isPrivate
    ? WidgetTemplatesVisibilityEnum.Prv
    : WidgetTemplatesVisibilityEnum.Pub;

  // Add description if provided
  const description = _get(values, 'description') as string | undefined;
  if (description) {
    attributes.description = description;
  }

  // Handle External Link specific fields
  if (kind === WidgetTemplatesKindEnum.ExternalLink) {
    const link = _get(values, 'link') as string | undefined;
    attributes.fields = {
      link: link || undefined,
    };
  }

  // Handle icon image attachment (for all widget kinds that support it)
  const iconImageValue = _get(values, 'iconImage') as
    | Record<string, unknown>
    | undefined;
  const iconAttachmentInputs = prepareAttachmentInputs(
    iconImageValue,
    undefined, // No original attachment for create
    AttachmentIntendedUse.WidgetIcon,
  );
  attachmentsToSend.push(...iconAttachmentInputs);

  // Handle Announcement specific fields
  if (kind === WidgetTemplatesKindEnum.Announcement) {
    const backgroundColor = _get(values, ['backgroundColor', 'value']) as
      | string
      | undefined;
    const content = _get(values, 'content') as string | undefined;
    attributes.fields = {
      backgroundColor: backgroundColor || undefined,
      content: content || undefined,
    };

    // Handle image attachment for announcement
    const imageValue = _get(values, 'image') as
      | Record<string, unknown>
      | undefined;
    const imageAttachmentInputs = prepareAttachmentInputs(
      imageValue,
      undefined, // No original attachment for create
      AttachmentIntendedUse.WidgetImage,
    );
    attachmentsToSend.push(...imageAttachmentInputs);
  }

  // Set attachmentsAttached only if there are attachments to send
  if (attachmentsToSend.length > 0) {
    attributes.attachmentsAttached =
      attachmentsToSend as WidgetTemplateCreateInputObject['attachmentsAttached'];
  }

  return attributes;
}

/**
 * Hook for managing widget template creation
 * Handles form submission, navigation, and mutation state
 */
export default function useWidgetTemplateCreate(): UseWidgetTemplateCreateResult {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [createMutation, { loading }] = useWidgetTemplateCreateMutation();

  // Navigate back to widget templates list
  const handleCancel = useCallback(() => {
    navigate(APP_URLS.dashboard.homeWidgets.path);
  }, [navigate]);

  // Submit form and create widget template
  const handleSubmit = useCallback(
    async (values: Values) => {
      const attributes = prepareWidgetTemplateAttributes(values);

      if (!attributes) return;

      try {
        const result = await createMutation({
          variables: { attributes },
        });

        // Get the created template ID from mutation result
        const createdTemplateId = result.data?.data?.id;

        // Show success toast
        toast({ content: t('home.create-widget-template-success') });

        // Redirect with selectTemplateId param so the new template is auto-selected
        if (createdTemplateId) {
          navigate(
            APP_URLS.dashboard.homeWidgets.getPathWithQuery({
              [GetUrlParams.SelectTemplateId]: createdTemplateId,
            }),
          );
        } else {
          navigate(APP_URLS.dashboard.homeWidgets.path);
        }
      } catch {
        // Show generic error toast for any unexpected failures
        // Note: GraphQL errors are displayed via mutation error state
        toast({ content: t('home.create-template-error') });
      }
    },
    [createMutation, navigate, t],
  );

  return {
    loading,
    handleCancel,
    handleSubmit,
  };
}
